import clsx from 'clsx'
import { useCallback, useRef, useState, type ChangeEventHandler, type FunctionComponent } from 'react'
import { useTranslate } from '../../contexts/TranslationContext'
import SvgCross from '../../svg/icons/Cross'
import SvgSearch from '../../svg/icons/Search'
import { Button } from './Button'
import { Hidden } from './Hidden'
import { LoadingSpinner } from './LoadingSpinner'
import style from './TitleEnumeration.module.sass'

type TitleEnumerationProps = {
	title: string
	sum: number | undefined
	buttonLabel?: string
	searchedText?: string
	onChange?: ChangeEventHandler<HTMLInputElement>
	handleClose?: () => void
	isLoading?: boolean
}

export const TitleEnumeration: FunctionComponent<TitleEnumerationProps> = ({
	title,
	sum,
	buttonLabel,
	onChange,
	handleClose,
	searchedText = '',
	isLoading,
}) => {
	const translate = useTranslate()
	const [isSearchVisible, setIsSearchVisible] = useState(false)

	const inputRef = useRef<HTMLInputElement>(null)

	const open = useCallback(() => {
		setIsSearchVisible(true)
		setTimeout(() => {
			inputRef.current?.focus()
		}, 200)
	}, [])

	const close = useCallback(() => {
		handleClose && handleClose()
		setIsSearchVisible(false)
		inputRef.current?.blur()
	}, [handleClose])

	return (
		<div className={clsx(style.root, buttonLabel && style.rootWithButton, isSearchVisible && style.rootBlock)}>
			<h2 className={style.title}>
				{title}: <span className={style.sum}>{sum}</span>
				<LoadingSpinner active={isLoading} />
			</h2>
			{buttonLabel && (
				<div className={style.searchButtonWrapper}>
					{isSearchVisible ? (
						<div className={clsx(style.searchInput, isSearchVisible && style.searchInputWithMargin)}>
							<input type="text" ref={inputRef} value={searchedText} className={style.input} onChange={onChange} />
							<div className={style.buttonWrapper}>
								<Button
									label={<SvgCross />}
									type={{ variant: 'transparent' }}
									icon={{ aloneStanding: true, fillPrimary: true }}
									onClick={close}
									ariaLabel={translate('titleEnumeration.close.button')}
								/>
							</div>
						</div>
					) : (
						<>
							<Hidden breakpoint="mobileDown">
								<Button
									label={buttonLabel}
									type={{ variant: 'bordered', borderRadiusSmall: true }}
									iconBefore={<SvgSearch />}
									ariaLabel={translate('button.search')}
									onClick={open}
								/>
							</Hidden>

							<Hidden breakpoint="mobileUp">
								<button
									type="button"
									aria-label={translate('button.search')}
									className={style.buttonMobile}
									onClick={open}
								>
									<SvgSearch />
								</button>
							</Hidden>
						</>
					)}
				</div>
			)}
		</div>
	)
}
